import React, { useEffect, useState } from "react";
import axios from "axios";

export default function AdminPanel() {
  const [gameName, setGameName] = useState("");
  const [games, setGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState("");
  const [unapprovedUsers, setUnapprovedUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);

  // Load games on mount + periodically refresh
  useEffect(() => {
    fetchGames();
    const interval = setInterval(fetchGames, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch users when a game is selected
  useEffect(() => {
    if (selectedGameId) {
      fetchUsers(selectedGameId);
    } else {
      setUnapprovedUsers([]);
      setApprovedUsers([]);
    }
  }, [selectedGameId]);

  // Fetch list of games from existing /api/games route
  const fetchGames = async () => {
    try {
      const res = await axios.get("/api/games", { withCredentials: true });
      setGames(res.data);
    } catch (err) {
      console.error("Failed to fetch games:", err);
    }
  };

  // Create a new game
  const createGame = async () => {
    if (!gameName.trim()) return alert("Enter a game name.");
    try {
      await axios.post(
        "/api/games",
        { name: gameName },
        { withCredentials: true }
      );
      setGameName("");
      fetchGames();
    } catch (err) {
      console.error("Failed to create game:", err);
      alert("Error creating game");
    }
  };

  // Fetch users for selected game
  const fetchUsers = async (gameId) => {
    try {
      const unapproved = await axios.get(
        `/api/games/${gameId}/unapproved-users`,
        { withCredentials: true }
      );
      const approved = await axios.get(
        `/api/games/${gameId}/approved-users`,
        { withCredentials: true }
      );

      setUnapprovedUsers(unapproved.data);
      setApprovedUsers(approved.data);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  };

  // Approve player
  const approve = async (userId) => {
    try {
      await axios.post(
        `/api/games/${selectedGameId}/approve/${userId}`,
        {},
        { withCredentials: true }
      );
      fetchUsers(selectedGameId);
      fetchGames();
    } catch (err) {
      console.error("Failed to approve user:", err);
      alert("Error approving player");
    }
  };

  // Start game
  const startGame = async () => {
    if (!selectedGameId) return alert("Select a game first.");
    try {
      await axios.post(
        `/api/games/${selectedGameId}/start`,
        {},
        { withCredentials: true }
      );
      alert("🚀 Game started!");
      fetchGames();
    } catch (err) {
      console.error("Failed to start game:", err);
      alert(err.response?.data?.error || "Error starting game");
    }
  };

  // End game
  const endGame = async () => {
    if (!selectedGameId) return alert("Select a game first.");

    try {
      const res = await axios.post(
        `/api/games/${selectedGameId}/end`,
        {},
        { withCredentials: true }
      );

      alert(`🏆 Game ended! Winner: ${res.data.winnerName}`);
      setSelectedGameId("");
      setApprovedUsers([]);
      setUnapprovedUsers([]);
      fetchGames();
    } catch (err) {
      console.error("Failed to end game:", err);
      alert(err.response?.data?.error || "Error ending game");
    }
  };

  // Helper: get approved count + status from game object
  const getApprovedCount = (game) =>
    Array.isArray(game.approvedPlayers) ? game.approvedPlayers.length : 0;

  const getStatusLabel = (game) => {
    const approvedCount = getApprovedCount(game);
    if (game.started) return { label: "🟢 Live", color: "#16a34a" };
    if (approvedCount > 0) return { label: "✅ Ready", color: "#2563eb" };
    return { label: "🕒 Waiting", color: "#d97706" };
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold">🛠️ Admin Panel</h2>

      {/* Create Game */}
      <div className="mt-4">
        <label className="block">Create New Game:</label>
        <input
          type="text"
          placeholder="Game name"
          value={gameName}
          onChange={(e) => setGameName(e.target.value)}
          className="border p-1"
        />
        <button
          onClick={createGame}
          className="ml-2 px-2 py-1 bg-blue-500 text-white rounded"
        >
          Create
        </button>
      </div>

      {/* Game Overview */}
      <div className="mt-6">
        <h3 className="font-semibold text-lg mb-2">🎮 Game Sessions</h3>

        {games.length === 0 ? (
          <p>No games created yet.</p>
        ) : (
          <div className="space-y-3">
            {games.map((game) => {
              const approvedCount = getApprovedCount(game);
              const { label, color } = getStatusLabel(game);

              return (
                <div
  key={game._id}
  style={{
    border: selectedGameId === game._id
      ? "3px solid #2563eb"     // 🔵 Blue border when selected
      : "2px solid #e5e7eb",
    background: selectedGameId === game._id
      ? "#eff6ff"              // 🔵 Light blue highlight
      : "#ffffff",
    borderRadius: "8px",
    padding: "12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "all 0.15s ease-in-out"
  }}
>

                  <div>
                    <div className="font-bold">{game.name}</div>
                    <div style={{ fontSize: "14px", color: "#374151" }}>
                      Approved Players: {approvedCount}
                    </div>
                  </div>

                  <div style={{ fontWeight: "bold", color }}>{label}</div>

                  <div>
                    <button
  onClick={() => {
    setSelectedGameId(game._id);
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  }}
  className="px-3 py-1 bg-blue-600 text-white rounded"
>
  Manage
</button>

                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Game Controls + Players for Selected Game */}
      {selectedGameId && (
        <>
          {/* Start / End Game */}
          <div className="mt-4 flex gap-3">
            <button
              onClick={startGame}
              className="px-4 py-2 bg-green-600 text-white rounded"
            >
              🚀 Start Game
            </button>

            <button
              onClick={endGame}
              className="px-4 py-2 bg-red-600 text-white rounded"
            >
              🛑 End Game
            </button>
          </div>

          {/* Approved Players */}
          <div className="mt-6">
            <h3 className="font-semibold text-lg">✅ Approved Players</h3>
            {approvedUsers.length === 0 ? (
              <p>No approved players yet.</p>
            ) : (
              <ul className="list-disc pl-6">
                {approvedUsers.map((u) => (
                  <li key={u._id}>{u.username}</li>
                ))}
              </ul>
            )}
          </div>

          {/* Approve New Players */}
          <div className="mt-6">
            <h3 className="font-semibold text-lg">📝 Approve New Players</h3>
            {unapprovedUsers.length === 0 ? (
              <p>No players to approve.</p>
            ) : (
              unapprovedUsers.map((u) => (
                <div
                  key={u._id}
                  className="flex items-center justify-between mt-2"
                >
                  <span>{u.username}</span>
                  <button
                    onClick={() => approve(u._id)}
                    className="bg-green-500 px-2 py-1 text-white rounded"
                  >
                    Approve
                  </button>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
